import { useState, useRef, useEffect } from 'react';
import { Clock, CheckCircle, AlarmClock, X } from 'lucide-react';
import { useReminders } from '@/contexts/ReminderContext';
import { formatDate } from '@/lib/dateFormat';

function formatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  if (diff < 0) {
    const mins = Math.floor(-diff / 60000);
    if (mins < 60) return `${mins}m overdue`;
    return `${Math.floor(mins / 60)}h overdue`;
  }
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  if (mins < 1440) return `in ${Math.floor(mins / 60)}h`;
  return formatDate(d);
}

export function ReminderBell() {
  const { pendingReminders, pendingCount, snooze, markDone, dismiss } = useReminders();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      {/* Clock icon with badge */}
      <button
        onClick={() => setOpen(v => !v)}
        className="relative h-9 w-9 flex items-center justify-center rounded-full text-[#64748B] hover:text-[#6366F1] hover:bg-indigo-50 transition-colors"
        title="Reminders"
      >
        <Clock className="h-5 w-5" />
        {pendingCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
            style={{ minWidth: 18, height: 18, padding: '0 4px' }}
          >
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-[340px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#6366F1]" />
              <span className="text-sm font-semibold text-[#1E293B]">Reminders</span>
              {pendingCount > 0 && (
                <span className="bg-[#6366F1] text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  {pendingCount}
                </span>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-[380px] overflow-y-auto">
            {pendingReminders.length === 0 ? (
              <div className="py-10 text-center">
                <Clock className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-[#64748B]">No upcoming reminders</p>
              </div>
            ) : (
              pendingReminders.map(r => {
                const isOverdue = new Date(r.dueAt) <= new Date();
                return (
                  <div key={r.id} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1E293B] truncate">{r.title}</p>
                        {r.lead && (
                          <p className="text-xs text-[#64748B] truncate mt-0.5">{r.lead.name}</p>
                        )}
                        <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isOverdue ? 'text-red-500' : 'text-[#64748B]'}`}>
                          <Clock className="h-3 w-3" />
                          {formatTime(r.dueAt)}
                        </div>
                      </div>
                      <button
                        onClick={() => dismiss(r.id)}
                        className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0 mt-0.5"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => snooze(r.id, 15)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-[#64748B] text-xs hover:bg-gray-200 transition-colors"
                      >
                        <AlarmClock className="h-3 w-3" /> 15m
                      </button>
                      <button
                        onClick={() => snooze(r.id, 60)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-[#64748B] text-xs hover:bg-gray-200 transition-colors"
                      >
                        <AlarmClock className="h-3 w-3" /> 1h
                      </button>
                      <button
                        onClick={() => markDone(r.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-xs hover:bg-emerald-100 transition-colors"
                      >
                        <CheckCircle className="h-3 w-3" /> Done
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
